import React, { useEffect, useMemo, useState, useCallback } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  TextField,
  Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import axios from 'axios'
import { useList } from '@refinedev/core'

interface OpportunityCategoryOption {
  value: string
  name: string
  description: string
}

const apiUrl = import.meta.env.VITE_API_URL

export interface RegenPanelProps {
  targetUuid: string
  /** merged-pending values: current record fields overlaid with proposed payload */
  mergedDescription: string
  mergedCriteria: string
  mergedExclusions: string
  mergedFundName: string
  /** current saved values for comparison */
  currentProfileText: string
  currentTags: string[]
  /** proposed edit id — used to set inline_regen_at marker after save (fs-3gx) */
  proposedEditId: number
  /**
   * The proposed_edit's current payload. On Save we amend the payload so the
   * regenerated profile_text and tags become part of the recorded change set
   * (fs-zb2) — otherwise they'd be invisible in the approved-edit diff.
   */
  currentPayload: Record<string, unknown>
  /**
   * Reports whether the panel holds generated/edited output that has NOT been
   * saved into the proposed_edit (GH#1630). The parent modal disables its
   * Approve/Reject while this is true so a stray click can't discard the work.
   */
  onUnsavedChange?: (dirty: boolean) => void
  /**
   * Reports whether the reviewer has completed (saved) a regeneration for this
   * edit. The parent modal uses it to gate Approve from the outset (GH#1849 P1)
   * — Approve stays disabled until the profile is regenerated-and-saved or the
   * reviewer explicitly skips.
   */
  onCompletedChange?: (completed: boolean) => void
}

/**
 * Panel rendered inside the proposed-edits review dialog when the selected
 * edit is for an opportunity and the diff includes description/criteria/exclusions.
 *
 * Allows the admin to regenerate the opportunity profile and tags inline,
 * review and edit the results, and persist them via PATCH before approving
 * the original proposed_edit.
 */
export const RegenPanel: React.FC<RegenPanelProps> = ({
  targetUuid,
  mergedDescription,
  mergedCriteria,
  mergedExclusions,
  mergedFundName,
  currentProfileText,
  currentTags,
  proposedEditId,
  currentPayload,
  onUnsavedChange,
  onCompletedChange,
}) => {
  const [driverFieldsExpanded, setDriverFieldsExpanded] = useState(false)

  const [profileText, setProfileText] = useState('')
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileError, setProfileError] = useState<string | null>(null)

  const [tagResults, setTagResults] = useState<{
    tags: string[]
    reasoning: string
  } | null>(null)
  const [tagsLoading, setTagsLoading] = useState(false)
  const [tagError, setTagError] = useState<string | null>(null)
  const [editableTags, setEditableTags] = useState<string[]>(currentTags)

  const [saveLoading, setSaveLoading] = useState(false)
  const [saveDone, setSaveDone] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Fetch the canonical OpportunityCategory list so the tag picker can
  // restrict entries to known values (fs-rd6). Goes through refine's
  // useList so the request waits for the auth context and is deduped
  // across all instances on the page.
  const categoriesResult = useList<OpportunityCategoryOption>({
    resource: 'opportunity_categories',
    queryOptions: { staleTime: 5 * 60 * 1000 },
  })
  const validTagOptions = useMemo<string[]>(
    () => (categoriesResult?.data?.data ?? []).map((o) => o.value),
    [categoriesResult],
  )

  /**
   * Tag generation parameterised on the profile to use. Called both from the
   * standalone "Regenerate Tags" button (uses current profileText state) and
   * from the combined profile+tags flow (uses the freshly-generated profile
   * before React state has had a chance to flush — GH#1515 #2).
   */
  const generateTagsFor = useCallback(
    async (profile: string) => {
      setTagsLoading(true)
      setTagError(null)
      try {
        const response = await axios.post(
          `${apiUrl}/opportunities/tag/${targetUuid}`,
          { profile_text: profile },
        )
        const result = response.data as { tags: string[]; reasoning: string }
        setTagResults(result)
        setEditableTags(result.tags)
      } catch (e: any) {
        const status = e?.response?.status
        if (status === 400) {
          setTagError(
            e?.response?.data?.detail ??
              'Cannot generate tags: opportunity profile is blank. Publish a profile first.',
          )
        } else {
          setTagError(
            e?.response?.data?.detail ?? e?.message ?? 'Failed to generate tags',
          )
        }
      } finally {
        setTagsLoading(false)
      }
    },
    [targetUuid],
  )

  /**
   * Primary action: regenerate the profile, then immediately regenerate tags
   * against the freshly-generated profile. Per GH#1515 #2 the two should
   * always run together so the human reviewer sees both at once and the
   * approved edit carries a consistent profile/tags pair.
   */
  const handleGenerateProfile = useCallback(async () => {
    setProfileLoading(true)
    setProfileError(null)
    try {
      const response = await axios.post(
        `${apiUrl}/opportunities/generate-profile/${targetUuid}`,
        {
          description: mergedDescription,
          criteria: mergedCriteria,
          exclusions: mergedExclusions,
          fund_name: mergedFundName,
        },
      )
      const nextProfile = response.data.profile ?? ''
      setProfileText(nextProfile)
      // Chain tags immediately. Pass the fresh profile directly — relying on
      // the profileText state would race the next render.
      if (nextProfile.trim()) {
        await generateTagsFor(nextProfile)
      }
    } catch (e: any) {
      const detail =
        e?.response?.data?.detail ??
        e?.message ??
        'Failed to generate profile'
      setProfileError(detail)
    } finally {
      setProfileLoading(false)
    }
  }, [
    targetUuid,
    mergedDescription,
    mergedCriteria,
    mergedExclusions,
    mergedFundName,
    generateTagsFor,
  ])

  /** Tags-only path used when the admin has hand-edited the profile and wants
   * tags refreshed without overwriting their text. */
  const handleGenerateTags = useCallback(async () => {
    await generateTagsFor(profileText)
  }, [profileText, generateTagsFor])

  const handleRemoveTag = useCallback((tag: string) => {
    setEditableTags((prev) => prev.filter((t) => t !== tag))
  }, [])

  const handleAddTag = useCallback(
    (tag: string | null) => {
      if (!tag) return
      if (!validTagOptions.includes(tag)) return
      setEditableTags((prev) =>
        prev.includes(tag) ? prev : [...prev, tag],
      )
    },
    [validTagOptions],
  )

  // Unsaved = there is generated/edited profile text that has not been saved
  // into the proposed_edit yet. Tag edits are only reachable while the editable
  // view is showing (saveDone === false with a non-empty profile), so this
  // predicate covers unsaved tag changes too.
  const dirty = profileText.trim() !== '' && !saveDone
  // Report unsaved state to the parent; the cleanup clears the parent's guard
  // when this panel unmounts while dirty.
  useEffect(() => {
    onUnsavedChange?.(dirty)
    return () => {
      onUnsavedChange?.(false)
    }
  }, [dirty, onUnsavedChange])

  // Report regen completion (a successful save) to the parent so it can unlock
  // Approve (GH#1849 P1). Cleared on unmount so switching edits re-gates.
  useEffect(() => {
    onCompletedChange?.(saveDone)
    return () => {
      onCompletedChange?.(false)
    }
  }, [saveDone, onCompletedChange])

  const handleDiscard = useCallback(() => {
    setProfileText('')
    setTagResults(null)
    setEditableTags(currentTags)
    setProfileError(null)
    setTagError(null)
    setSaveError(null)
    setSaveDone(false)
  }, [currentTags])

  const handleSave = useCallback(async () => {
    setSaveLoading(true)
    setSaveError(null)
    setSaveDone(false)
    try {
      // Amend the proposed_edit payload so the regenerated profile_text +
      // tags get applied along with the rest of the edit on approval
      // (fs-zb2 + fs-wem). The direct PATCH /opportunities call that used
      // to live here was removed — the apply path handles persistence.
      // Only include `tags` when the admin actually regenerated them;
      // otherwise the existing opportunity tags would be overwritten by
      // the editable list (which mirrors currentTags but may not reflect
      // intent if regen wasn't run).
      const payloadAdditions: Record<string, unknown> = {
        profile_text: profileText,
      }
      if (tagResults) {
        payloadAdditions.tags = editableTags
      }
      await axios.patch(`${apiUrl}/proposed-edits/${proposedEditId}`, {
        payload: { ...currentPayload, ...payloadAdditions },
      })
      setSaveDone(true)
      // Record inline_regen_at on the proposed_edit so the apply handler can
      // detect that regen happened and skip emitting a profile-regen advisory.
      // Failure here is non-fatal: the payload amend already succeeded; the
      // advisory will just be emitted normally as a conservative fallback.
      try {
        await axios.post(
          `${apiUrl}/proposed-edits/${proposedEditId}/inline-regen-marker`,
        )
      } catch (markerErr: any) {
        console.warn(
          '[RegenPanel] inline-regen-marker call failed (save still succeeded):',
          markerErr?.response?.data?.detail ?? markerErr?.message ?? markerErr,
        )
      }
    } catch (e: any) {
      const raw = e?.response?.data?.detail ?? e?.message ?? 'Failed to save'
      setSaveError(typeof raw === 'string' ? raw : JSON.stringify(raw))
    } finally {
      setSaveLoading(false)
    }
  }, [profileText, editableTags, tagResults, proposedEditId, currentPayload])

  return (
    <Box
      sx={{
        mt: 3,
        pt: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        Profile + Tag Regeneration
      </Typography>

      {saveDone ? (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="success.main">
            Profile and tags saved to this proposed edit. They will be applied
            when the edit is approved.
          </Typography>
          <Button
            size="small"
            variant="text"
            onClick={() => setSaveDone(false)}
            sx={{ textTransform: 'none' }}
          >
            Edit again
          </Button>
        </Box>
      ) : (
        <>
      {/* Driver fields collapsible */}
      <Box sx={{ mb: 2 }}>
        <Button
          size="small"
          variant="text"
          startIcon={
            driverFieldsExpanded ? (
              <KeyboardArrowDownIcon fontSize="small" />
            ) : (
              <KeyboardArrowRightIcon fontSize="small" />
            )
          }
          onClick={() => setDriverFieldsExpanded((v) => !v)}
          sx={{ textTransform: 'none', p: 0, color: 'text.secondary' }}
        >
          Driver fields used
        </Button>
        <Collapse in={driverFieldsExpanded}>
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              bgcolor: 'grey.50',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <TextField
              label="Description"
              value={mergedDescription}
              multiline
              minRows={3}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Criteria"
              value={mergedCriteria}
              multiline
              minRows={3}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Exclusions"
              value={mergedExclusions}
              multiline
              minRows={2}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Box>
        </Collapse>
      </Box>

      {/* Generate Profile */}
      <Box sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleGenerateProfile}
          disabled={profileLoading}
          startIcon={profileLoading ? <CircularProgress size={16} /> : null}
        >
          {profileLoading
            ? 'Generating profile + tags…'
            : profileText
              ? 'Regenerate Profile + Tags'
              : 'Generate Profile + Tags'}
        </Button>

        {profileError && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {profileError}
          </Typography>
        )}

        {profileText && (
          <Box sx={{ mt: 1.5 }}>
            {currentProfileText && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Edit generated profile (current saved profile exists — saving
                will overwrite it):
              </Typography>
            )}
            <TextField
              label="Profile text"
              value={profileText}
              onChange={(e) => {
                setProfileText(e.target.value)
                setSaveDone(false)
              }}
              multiline
              minRows={6}
              size="small"
              fullWidth
            />
          </Box>
        )}
      </Box>

      {/* Tags-only refresh — for when the admin has edited the profile by hand
          and just wants new tags reflecting their edits. The primary
          "Regenerate Profile + Tags" button above re-runs both. */}
      <Box sx={{ mt: 2 }}>
        <Button
          variant="text"
          size="small"
          onClick={handleGenerateTags}
          disabled={tagsLoading || !profileText.trim()}
          startIcon={tagsLoading ? <CircularProgress size={16} /> : null}
          sx={{ textTransform: 'none' }}
        >
          {tagsLoading
            ? 'Regenerating tags…'
            : 'Regenerate tags only (use after editing profile)'}
        </Button>

        {tagError && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {tagError}
          </Typography>
        )}

        {tagResults && (
          <Box sx={{ mt: 1.5 }}>
            {/* Current tags */}
            {currentTags.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.5 }}
                >
                  Current tags:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {currentTags.map((tag) => (
                    <Chip key={tag} label={tag} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}

            {/* New tags from regen */}
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                New tags (from regeneration):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {tagResults.tags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" color="primary" variant="outlined" />
                ))}
              </Box>
            </Box>

            {/* Reasoning */}
            {tagResults.reasoning && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1.5, fontStyle: 'italic' }}
              >
                Reasoning: {tagResults.reasoning}
              </Typography>
            )}

            {/* Editable tags to save */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Tags to save:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {editableTags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    onDelete={() => handleRemoveTag(tag)}
                  />
                ))}
              </Box>
              <Autocomplete
                options={validTagOptions.filter(
                  (opt) => !editableTags.includes(opt),
                )}
                value={null}
                blurOnSelect
                clearOnBlur
                size="small"
                sx={{ minWidth: 220 }}
                onChange={(_event, value) => handleAddTag(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Add tag" />
                )}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Save */}
      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={saveLoading || !profileText.trim() || saveDone}
        >
          {saveLoading
            ? 'Saving...'
            : saveDone
              ? 'Saved!'
              : 'Save profile + tags'}
        </Button>

        {dirty && (
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={handleDiscard}
            disabled={saveLoading}
            sx={{ ml: 1, textTransform: 'none' }}
          >
            Discard
          </Button>
        )}

        {saveError && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {saveError}
          </Typography>
        )}
      </Box>
        </>
      )}
    </Box>
  )
}
